// src/pages/ExchangePage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { FiArrowRight } from 'react-icons/fi';
import { useBalance } from '../context/BalanceContext';
import { usePrices } from '../context/PriceContext';
import { db } from '../firebase/config';
import { collection, serverTimestamp, doc, runTransaction, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { formatCrypto } from '../utils/numberFormat';
import CoinSelectorControl from '../components/ui/CoinSelectorControl';
import { useNotification } from '../contexts/NotificationContext';
import './ExchangePage.css';

const normalizeSymbol = (symbol = '') => symbol.toUpperCase();

const findBalanceKey = (balances, symbol) => {
  if (!balances || !symbol) return null;
  const upper = normalizeSymbol(symbol);
  return Object.keys(balances).find((key) => key.toUpperCase() === upper) || null;
};

const getNumericBalanceFromMap = (balances, symbol) => {
  if (!balances || !symbol) return 0;
  const key = findBalanceKey(balances, symbol);
  if (!key) return 0;
  const value = Number(balances[key]);
  return Number.isFinite(value) ? value : 0;
};

const setRoundedAmount = (value) => Number(Number(value).toFixed(8));

const ExchangePage = () => {
  // Initialize with default values to prevent undefined errors
  const defaultBalanceData = {
    accountBalances: { trading: {}, funding: {}, arbitrage: {} },
    lockedFunds: {}
  };
  
  // —— Context for shared balances ——
  const { balanceData = defaultBalanceData } = useBalance();

  const userCoinBalances = useMemo(() => {
    const combined = {};
    const accounts = balanceData?.accountBalances || {};

    Object.entries(accounts).forEach(([accountKey, coins]) => {
      Object.entries(coins || {}).forEach(([coinKey, rawAmount]) => {
        const symbol = normalizeSymbol(coinKey);
        if (!combined[symbol]) {
          combined[symbol] = { funding: 0, trading: 0, arbitrage: 0, total: 0 };
        }

        const numeric = Number(rawAmount);
        if (!Number.isFinite(numeric)) {
          return;
        }

        if (typeof combined[symbol][accountKey] !== 'number') {
          combined[symbol][accountKey] = 0;
        }

        combined[symbol][accountKey] += numeric;
        combined[symbol].total += numeric;
      });
    });

    return combined;
  }, [balanceData]);

  const getAvailableBalance = useCallback((symbol) => {
    if (!symbol) return 0;
    const normalized = normalizeSymbol(symbol);
    const entry = userCoinBalances[normalized];
    if (!entry) return 0;
    if (entry.funding) return entry.funding;
    if (entry.trading) return entry.trading;
    if (entry.arbitrage) return entry.arbitrage;
    return 0;
  }, [userCoinBalances]);

  const getSourceAccount = useCallback((symbol) => {
    if (!symbol) return null;
    const normalized = normalizeSymbol(symbol);
    const entry = userCoinBalances[normalized];
    if (!entry) return null;
    if (entry.funding) return 'Funding';
    if (entry.trading) return 'Trading';
    if (entry.arbitrage) return 'Arbitrage';
    return null;
  }, [userCoinBalances]);

  const getFundingBalance = useCallback((symbol) => {
    if (!symbol) return 0;
    const normalized = normalizeSymbol(symbol);
    const entry = userCoinBalances[normalized];
    if (!entry) return 0;
    return entry.funding || 0;
  }, [userCoinBalances]);

  // —— Local state for exchange UI ——
  const [fromCoin, setFromCoin] = useState('USDT');
  const [toCoin, setToCoin] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [exchangeRate, setExchangeRate] = useState(0);
  const [estimatedAmount, setEstimatedAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingExchange, setPendingExchange] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Get live prices from context
  const { prices, allCoins, loading: pricesLoading } = usePrices();

  const { currentUser } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();

  const coinOptions = useMemo(() => {
    const list = [];
    if (allCoins && allCoins.length > 0) {
      list.push(...allCoins.map((c) => ({ symbol: c.symbol.toUpperCase(), name: c.name })));
    } else if (prices) {
      list.push(...Object.keys(prices).map((sym) => ({ symbol: sym.toUpperCase(), name: sym.toUpperCase() })));
    }
    Object.keys(userCoinBalances || {}).forEach((sym) => {
      if (!list.some((option) => option.symbol === sym)) {
        list.push({ symbol: sym, name: sym });
      }
    });
    ['USDT', 'USDC', 'BTC', 'ETH'].forEach((sym) => {
      if (!list.some((option) => option.symbol === sym)) {
        list.push({ symbol: sym, name: sym });
      }
    });
    const unique = new Map();
    list.forEach((item) => {
      if (!unique.has(item.symbol)) unique.set(item.symbol, item);
    });
    return Array.from(unique.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [allCoins, prices, userCoinBalances]);

  const coinSelectorOptions = useMemo(() => {
    return coinOptions.map((option) => {
      const match = (allCoins || []).find((coin) => coin.symbol?.toUpperCase() === option.symbol);
      const symbol = option.symbol.toUpperCase();
      return {
        value: symbol,
        label: `${symbol}`,
        symbol,
        icon: match?.image ?? null
      };
    });
  }, [allCoins, coinOptions]);

  /* Removed static `coins` array previously hard-coded here */
  /* const coins = [...] */


  // We no longer need to fetch prices here as they come from the PriceContext

  // —— Recompute rate & estimate when inputs change ——  
  useEffect(() => {
    if (prices && prices[fromCoin] && prices[toCoin]) {
      const rate = prices[fromCoin] / prices[toCoin];
      setExchangeRate(rate);
      setEstimatedAmount(amount ? parseFloat(amount) * rate : 0);
    }
  }, [fromCoin, toCoin, prices, amount]);

  // Fetch and subscribe to exchange history from Firestore
  useEffect(() => {
    if (!currentUser) return;
    
    // Function to fetch exchange history from Firestore
    const fetchExchangeHistory = async () => {
      try {
        // Query the exchangeHistory collection for this user
        const exchangeHistoryRef = collection(db, 'exchangeHistory');
        const q = query(
          exchangeHistoryRef,
          where('userId', '==', (currentUser.uid || currentUser.id))
        );
        
        const querySnapshot = await getDocs(q);
        const historyData = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
          .slice(0, 20);
        setHistory(historyData);
      } catch (error) {
        console.error('Error fetching exchange history:', error);
        setHistory([]); // Default to empty on error
      }
    };
    
    fetchExchangeHistory();
    
    // Set up real-time listener for exchange history updates
    const exchangeHistoryRef = collection(db, 'exchangeHistory');
    const q = query(
      exchangeHistoryRef,
      where('userId', '==', (currentUser.uid || currentUser.id))
    );
    
    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const historyData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
        .slice(0, 20);
      setHistory(historyData);
    }, (error) => {
      console.error('Error in exchange history subscription:', error);
    });
    
    // Clean up listener on unmount
    return () => unsubscribe();
  }, [currentUser]);

  // —— Handlers ——  
  const handleSwitchCoins = () => {
    setFromCoin(toCoin);
    setToCoin(fromCoin);
    setAmount('');
    setMessage({ text: '', type: '' });
  };

  const handleMaxAmount = () => {
    const available = getAvailableBalance(fromCoin);
    if (!available) {
      setAmount('');
      return;
    }
    setAmount(setRoundedAmount(available).toString());
  };

  const executeExchange = async ({ fromSymbol, toSymbol, amountValue }) => {
    setLoading(true);
    try {
      const upperFrom = fromSymbol.toUpperCase();
      const upperTo = toSymbol.toUpperCase();
      const rate =
        prices?.[upperFrom] && prices?.[upperTo]
          ? prices[upperFrom] / prices[upperTo]
          : exchangeRate || 0;

      if (!rate) {
        throw new Error('Unable to compute exchange rate. Please try again.');
      }

      const receivedAmount = amountValue * rate;
      const userProfileRef = doc(db, 'userProfiles', currentUser.uid);

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userProfileRef);

        if (!userDoc.exists()) {
          throw new Error("User profile doesn't exist!");
        }

        const userData = userDoc.data() || {};
        const accountBalances = userData.accountBalances || {};
        const fundingAccount = { ...(accountBalances.funding || {}) };
        const tradingAccount = { ...(accountBalances.trading || {}) };
        const arbitrageAccount = { ...(accountBalances.arbitrage || {}) };

        const fundingBalanceAvailable = getNumericBalanceFromMap(fundingAccount, upperFrom);
        const tradingBalanceAvailable = getNumericBalanceFromMap(tradingAccount, upperFrom);
        const arbitrageBalanceAvailable = getNumericBalanceFromMap(arbitrageAccount, upperFrom);

        let sourceType = null;
        if (fundingBalanceAvailable >= amountValue) {
          sourceType = 'funding';
        } else if (tradingBalanceAvailable >= amountValue) {
          sourceType = 'trading';
        } else if (arbitrageBalanceAvailable >= amountValue) {
          sourceType = 'arbitrage';
        } else {
          throw new Error(`Insufficient ${fromSymbol} balance`);
        }

        const applyDelta = (account, symbol, delta) => {
          const key = findBalanceKey(account, symbol) || symbol;
          const current = getNumericBalanceFromMap(account, symbol);
          const next = setRoundedAmount(current + delta);
          if (next < -1e-8) {
            throw new Error(`Insufficient ${symbol} balance in ${symbol === upperFrom ? sourceType : 'funding'} account`);
          }
          account[key] = next < 0 ? 0 : next;
        };

        if (sourceType === 'funding') {
          applyDelta(fundingAccount, upperFrom, -amountValue);
        } else if (sourceType === 'trading') {
          applyDelta(tradingAccount, upperFrom, -amountValue);
        } else {
          applyDelta(arbitrageAccount, upperFrom, -amountValue);
        }

        applyDelta(fundingAccount, upperTo, receivedAmount);

        transaction.update(userProfileRef, {
          accountBalances: {
            ...accountBalances,
            funding: fundingAccount,
            trading: tradingAccount,
            arbitrage: arbitrageAccount
          },
          updatedAt: serverTimestamp(),
          lastExchange: {
            from: upperFrom,
            to: upperTo,
            amount: amountValue,
            receivedAmount,
            rate,
            timestamp: serverTimestamp()
          }
        });
        const exchangeHistoryRef = collection(db, 'exchangeHistory');
        transaction.set(doc(exchangeHistoryRef), {
          userId: currentUser.uid,
          userEmail: currentUser.email,
          fromCoin: fromSymbol,
          toCoin: toSymbol,
          amountFrom: amountValue,
          receivedAmount,
          exchangeRate: rate,
          status: 'completed',
          timestamp: serverTimestamp()
        });
      });

      setExchangeRate(rate);
      setEstimatedAmount(receivedAmount);
      const successCopy = `Exchanged ${formatCrypto(amountValue, fromSymbol)} to ${formatCrypto(receivedAmount, toSymbol)}`;
      setMessage({ text: `Success! ${successCopy}`, type: 'success' });
      showSuccess(successCopy);
      setAmount('');
      return true;
    } catch (error) {
      console.error('Error processing exchange:', error);
      const errorMessage = error?.message || 'Exchange failed';
      setMessage({ text: `Exchange failed: ${errorMessage}`, type: 'error' });
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
      setPendingExchange(null);
      setAgreedToTerms(false);
    }
  };

  const handleExchange = async () => {
    setMessage({ text: '', type: '' });
    if (fromCoin === toCoin) {
      const warning = 'Select two different assets to exchange.';
      setMessage({ text: warning, type: 'error' });
      showWarning(warning);
      return;
    }

    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      const warning = 'Please enter a valid amount greater than zero.';
      setMessage({ text: warning, type: 'error' });
      showWarning(warning);
      return;
    }

    const availableBalance = getAvailableBalance(fromCoin);
    if (amt > availableBalance) {
      const warning = `Insufficient ${fromCoin} balance (Available: ${availableBalance.toFixed(8)})`;
      setMessage({ text: warning, type: 'error' });
      showWarning(warning);
      return;
    }

    if (!currentUser) {
      const warning = 'You must be logged in to exchange assets.';
      setMessage({ text: warning, type: 'error' });
      showWarning(warning);
      return;
    }

    if (!agreedToTerms) {
      setPendingExchange({ from: fromCoin, to: toCoin, amount: amt });
      setShowAgreementModal(true);
      showInfo('Review the exchange terms and accept to continue.');
      return;
    }

    await executeExchange({ fromSymbol: fromCoin, toSymbol: toCoin, amountValue: amt });
  };

  return (
    <div className="exchange-page">
      <div className="exchange-header">
        <div className="page-heading-gradient">Currency Exchange</div>
        {/* Removed refresh button as requested */}
      </div>

      <div className="exchange-container">
        <div className="exchange-card">
          {/* From selector */}
          <div className="coin-input-group simple-exchange-row">
            <div className="w-full">
              <CoinSelectorControl
                label="From asset"
                value={fromCoin}
                onChange={(nextValue) => {
                  setFromCoin(nextValue);
                  if (nextValue === toCoin) {
                    const fallback = coinSelectorOptions.find((option) => option.value !== nextValue)?.value;
                    if (fallback) {
                      setToCoin(fallback);
                    }
                  }
                }}
                options={coinSelectorOptions}
                disabled={loading || pricesLoading}
              />
            </div>
            <div className="balance-display-simple">
              <span>{formatCrypto(getAvailableBalance(fromCoin), fromCoin)}</span>
              {getSourceAccount(fromCoin) && (
                <span className="ml-1.5 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs font-medium text-slate-400">
                  from {getSourceAccount(fromCoin)}
                </span>
              )}
            </div>
          </div>

          {/* Switch */}
          <button
            className="switch-btn simple-switch-btn"
            onClick={handleSwitchCoins}
            disabled={loading}
            aria-label="Switch From and To"
          >
            <FiArrowRight size={22} />
          </button>

          {/* To selector */}
          <div className="coin-input-group simple-exchange-row">
            <div className="w-full">
              <CoinSelectorControl
                label="To asset"
                value={toCoin}
                onChange={(nextValue) => {
                  setToCoin(nextValue);
                  if (nextValue === fromCoin) {
                    const fallback = coinSelectorOptions.find((option) => option.value !== nextValue)?.value;
                    if (fallback) {
                      setFromCoin(fallback);
                    }
                  }
                }}
                options={coinSelectorOptions}
                disabled={loading || pricesLoading}
              />
            </div>
            <div className="balance-display-simple">
              {formatCrypto(getFundingBalance(toCoin), toCoin)}
            </div>
          </div>

          {/* Amount input */}
          <div className="amount-section">
            <label>Amount</label>
            <div className="amount-input-wrapper">
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                disabled={loading}
              />
              <button
                className="max-btn"
                onClick={handleMaxAmount}
                disabled={loading}
              >
                MAX
              </button>
            </div>
          </div>

          {/* Rate & Estimate */}
          <div className="rate-info">
            <div>
              <strong>Rate:</strong> 1 {fromCoin} = {formatCrypto(exchangeRate, toCoin)}
            </div>
            <div>
              <strong>Receive:</strong> {formatCrypto(estimatedAmount, toCoin)}
            </div>
          </div>

          {/* Messages */}
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Exchange button */}
          <button
            className="exchange-btn"
            onClick={handleExchange}
            disabled={loading || !amount}
          >
            {loading ? 'Processing...' : 'Exchange'}
          </button>
        </div>
      </div>

      {/* Exchange History */}
      <div className="exchange-history mt-12 p-6 md:p-8 bg-slate-800/60 rounded-xl border border-white/10">
        <h3>Exchange History</h3>
        {history.length === 0 ? (
          <div className="empty-history">No exchange history yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="history-table min-w-[560px] w-full text-xs md:text-sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>From</th>
                <th>To</th>
                <th>Amount</th>
                <th>Received</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {history.map(item => (
                <tr key={item.id}>
                  <td>{new Date(((item.timestamp?.seconds || item.createdAt?.seconds || 0) * 1000)).toLocaleString('en-US', { timeZone: 'UTC', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                  <td>{item.fromCoin}</td>
                  <td>{item.toCoin}</td>
                  <td>{formatCrypto(item.amountFrom, item.fromCoin)}</td>
                  <td>{formatCrypto(item.receivedAmount, item.toCoin)}</td>
                  <td>{formatCrypto(item.exchangeRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {showAgreementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-gray-900 p-6 text-white">
            <h3 className="text-xl font-semibold">Exchange Agreement</h3>
            <p className="mt-3 text-sm text-gray-300">
              Please review and accept the terms before completing your exchange.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-gray-200">
              <li>• All exchanges settle funds back into your funding wallet.</li>
              <li>• Exchange rates are sourced from live market data and may fluctuate.</li>
              <li>• Completed exchanges are final and cannot be reversed automatically.</li>
            </ul>
            <label className="mt-5 flex items-center gap-2 text-sm text-gray-200">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(event) => setAgreedToTerms(event.target.checked)}
                className="h-4 w-4 rounded border-white/20 bg-black/40"
              />
              I have reviewed the exchange terms and authorize this transfer.
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAgreementModal(false);
                  setPendingExchange(null);
                  setAgreedToTerms(false);
                }}
                className="rounded-xl border border-white/20 px-4 py-2 text-sm text-gray-300 transition hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!agreedToTerms || !pendingExchange) return;
                  setShowAgreementModal(false);
                  setAmount(pendingExchange.amount.toString());
                  await handleExchange();
                }}
                disabled={!agreedToTerms || loading}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  !agreedToTerms || loading ? 'cursor-not-allowed bg-blue-500/40 text-white/60' : 'bg-blue-500 text-white hover:bg-blue-400'
                }`}
              >
                Agree & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangePage;
